import { ApolloQueryResult } from 'apollo-client';

import {
    getValueFromApiFormat,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import { mapRowsToUsersProfile, mapUserProfileFilterToApiFormat } from '../../mappers/usersProfile';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import {
    GdRetargetingConditionTypeInput,
    RetargetingsSuggestQuery,
    UsersProfileQuery
} from '../../typings/graphql-grid';
import { defaultClientInfo, GridSourceParams } from '../../typings/grid';

import { mapGridParamsToStatRequirements } from './common';

export const mapUsersProfileQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<UsersProfileQuery>,
    params: GridSourceParams
) => {
    const { client, reqId } = result.data;
    const cryptaSegments = result.data.cryptaSegments.rowset;
    const {
        goals,
        retargetings,
        info: { workCurrency, countryRegionId }
    } = client;
    const { rowset: usersProfileRows, totalCount, totalStats, retargetingIds, cacheKey } = retargetings;
    const { rowset: goalsFromApi } = goals;
    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToUsersProfile(usersProfileRows, cryptaSegments, goalsFromApi, client, params),
            total: totalCount,
            allItemsIds: retargetingIds.map(String),
            cacheKey: getValueFromApiFormat(cacheKey)
        },
        clientInfo: {
            ...defaultClientInfo,
            workCurrency,
            countryRegionId
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        reqId,
        hasObjectsOverLimit: retargetings.hasObjectsOverLimit
    };
};

export const mapSourceParamsToUserProfileQueryVariables = (params: GridSourceParams) => {
    const { sortParams, view, campaignsIds, groupsIds, filter } = params;

    return {
        statRequirements: mapGridParamsToStatRequirements(params),
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        filter: {
            ...mapUserProfileFilterToApiFormat(filter),
            typeIn: [GdRetargetingConditionTypeInput.INTERESTS],
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapSourceParamsToUsersProfileAndSuggestQueryVariables = (params: GridSourceParams) => {
    const { campaignsIds, groupsIds, statPeriod, sortParams, view } = params;
    let statRequirements;

    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;

        statRequirements = {
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        statRequirements,
        filter: {
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapUsersProfileSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<RetargetingsSuggestQuery>
) => {
    return {
        data: result.data.client.retargetings.rowset.reduce((acc: string[], item) => {
            if (item.retargetingCondition !== null) {
                acc.push(item.retargetingCondition.name);
            }

            return acc;
        }, []),
        reqId: result.data.reqId
    };
};
